const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "just now", "4 min ago", "3 h ago", "2 d ago"; "in 5 min" for the future. */
export function timeAgo(value: string | Date | null | undefined, now = Date.now()): string {
  if (!value) return "—";
  const diff = now - new Date(value).getTime();
  const future = diff < 0;
  const ms = Math.abs(diff);
  let text: string;
  if (ms < 45_000) return future ? "in a moment" : "just now";
  if (ms < HOUR) text = `${Math.round(ms / MINUTE)} min`;
  else if (ms < DAY) text = `${Math.round(ms / HOUR)} h`;
  else text = `${Math.round(ms / DAY)} d`;
  return future ? `in ${text}` : `${text} ago`;
}

export function dateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

/** 950 → "950 ms", 12_300 → "12.3 s", 185_000 → "3 min 5 s". */
export function duration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < MINUTE) return `${(ms / 1000).toFixed(ms < 10_000 ? 2 : 1)} s`;
  const m = Math.floor(ms / MINUTE);
  const s = Math.round((ms % MINUTE) / 1000);
  if (m < 60) return s ? `${m} min ${s} s` : `${m} min`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
}

/** How long an alert has been (or was) open. */
export function openFor(from: string, to?: string | null): string {
  return duration(new Date(to ?? Date.now()).getTime() - new Date(from).getTime());
}

/** A plain-words reading of the cron patterns people actually use; the raw expression otherwise. */
export function describeCron(cron: string | null | undefined): string {
  if (!cron) return "Not scheduled";
  const parts = cron.trim().split(/\s+/);
  const fields = parts.length === 6 ? parts.slice(1) : parts;
  const [min, hour, dom, mon, dow] = fields;
  const everyday = dom === "*" && mon === "*" && dow === "*";
  if (parts.length === 6 && parts[0]!.startsWith("*/") && min === "*" && hour === "*" && everyday) return `Every ${parts[0]!.slice(2)} s`;
  if (min === "*" && hour === "*" && everyday) return "Every minute";
  if (min?.startsWith("*/") && hour === "*" && everyday) return `Every ${min.slice(2)} min`;
  if (/^\d+$/.test(min ?? "") && hour === "*" && everyday) return "Hourly";
  if (/^\d+$/.test(min ?? "") && hour?.startsWith("*/") && everyday) return `Every ${hour.slice(2)} h`;
  if (/^\d+$/.test(min ?? "") && /^\d+$/.test(hour ?? "") && everyday) return `Daily at ${hour!.padStart(2, "0")}:${min!.padStart(2, "0")}`;
  return cron;
}

export const plural = (n: number, word: string, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;
